import { Component, Input, ChangeDetectorRef, inject, input, output, OnInit, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { SortType } from '../../types/sort.type';
import { SelectionType } from '../../types/selection.type';
import { TableColumn } from '../../types/table-column.type';
import { nextSortDir } from '../../utils/sort';
import { SortDirection } from '../../types/sort-direction.type';
import { SortPropDir } from '../../types/sort-prop-dir.type';

interface HeaderCellContext {
  column: TableColumn;
  sortDir: SortDirection;
  sortFn: () => void;
  allRowsSelected: boolean;
  selectFn: (value: boolean) => void;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'datatable-header-cell',
  template: `
    <div class="datatable-header-cell-template-wrap">
      @if (isTarget()) {
        <ng-template [ngTemplateOutlet]="targetMarkerTemplate()"
                     [ngTemplateOutletContext]="targetMarkerContext()" />
      }
      @if (isCheckboxable) {
        <label class="datatable-checkbox">
          <input type="checkbox"
                 [checked]="allRowsSelected"
                 (change)="select.emit(!allRowsSelected)"/>
        </label>
      }
      @if (!column.headerTemplate) {
        <span class="datatable-header-cell-wrapper">
        <a href="javascript:void(0)"
           tabindex="0"
           role="button"
           class="datatable-header-cell-label draggable"
           (click)="onSort()"
           [innerHTML]="name">
        </a>
      </span>
      }
      @if (column.headerTemplate) {
        <ng-template [ngTemplateOutlet]="column.headerTemplate"
          [ngTemplateOutletContext]="cellContext"
         />
      }
    </div>
  `,
  host: {
    class: 'datatable-header-cell',
    '[style.height.px]': 'headerHeight()',
    '(contextmenu)': 'onContextmenu($event)',
    '[class]': 'columnCssClasses',
    '[attr.title]': 'column?.headerTemplate === undefined ? column?.name : undefined',
    '[style.minWidth.px]': 'column?.minWidth',
    '[style.maxWidth.px]': 'column?.maxWidth',
    '[style.width.px]': 'column?.width',
    '[attr.aria-sort]': 'ariaSortLabel()'
  },
  imports: [NgTemplateOutlet]
})
export class DataTableHeaderCellComponent implements OnInit {
  private readonly cd = inject(ChangeDetectorRef);

  readonly sortType = input<SortType>(undefined);
  readonly sortAscendingIcon = input<string>(undefined);
  readonly sortDescendingIcon = input<string>(undefined);
  readonly sortUnsetIcon = input<string>(undefined);

  readonly isTarget = input<boolean>(undefined);
  readonly targetMarkerTemplate = input<TemplateRef<unknown>>(undefined);
  readonly targetMarkerContext = input<unknown>(undefined);

  _allRowsSelected: boolean;

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set allRowsSelected(value: boolean) {
    this._allRowsSelected = value;
    this.cellContext.allRowsSelected = value;
  }

  get allRowsSelected(): boolean {
    return this._allRowsSelected;
  }

  readonly selectionType = input<SelectionType>(undefined);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set column(column: TableColumn) {
    this._column = column;
    this.cellContext.column = column;
    this.cd.markForCheck();
  }

  get column(): TableColumn {
    return this._column;
  }

  readonly headerHeight = input<number>(undefined);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set sorts(val: Array<SortPropDir>) {
    this._sorts = val;
    this.sortDir = this.calcSortDir(val);
    this.cellContext.sortDir = this.sortDir;
    this.sortClass = this.calcSortClass(this.sortDir);
    this.cd.markForCheck();
  }

  get sorts(): Array<SortPropDir> {
    return this._sorts;
  }

  readonly sort = output<{ column: TableColumn; prevValue: unknown; newValue: unknown }>();
  readonly select = output<unknown>();
  readonly columnContextmenu = output<{
    event: MouseEvent;
    column: unknown;
}>();

  get columnCssClasses(): unknown {
    let cls = 'datatable-header-cell';

    if (this.column.sortable) {
      cls += ' sortable';
    }
    if (this.column.resizeable) {
      cls += ' resizeable';
    }
    if (this.column.headerClass) {
      if (typeof this.column.headerClass === 'string') {
        cls += ' ' + this.column.headerClass;
      } else if (typeof this.column.headerClass === 'function') {
        const res = this.column.headerClass({
          column: this.column
        });

        if (typeof res === 'string') {
          cls += res;
        } else if (typeof res === 'object') {
          const keys = Object.keys(res);
          for (const k of keys) {
            if (res[k] === true) {
              cls += ` ${k}`;
            }
          }
        }
      }
    }

    const sortDir = this.sortDir;
    if (sortDir) {
      cls += ` sort-active sort-${sortDir}`;
    }

    return cls;
  }

  get name(): string {
    return this.column?.name;
  }

  get isCheckboxable(): boolean {
    return this.column.checkboxable && this.column.headerCheckboxable && this.selectionType() === SelectionType.checkbox;
  }

  sortFn = this.onSort.bind(this);
  sortClass: string;
  sortDir: SortDirection;
  selectFn = this.select.emit.bind(this.select);

  cellContext: HeaderCellContext;

  private _column: TableColumn;
  private _sorts: Array<SortPropDir>;

  constructor() {
    this.cellContext = {
      column: this.column,
      sortDir: this.sortDir,
      sortFn: this.sortFn,
      allRowsSelected: this.allRowsSelected,
      selectFn: this.selectFn
    };
  }

  onContextmenu($event: MouseEvent): void {
    this.columnContextmenu.emit({ event: $event, column: this.column });
  }

  ngOnInit(): void {
    this.sortClass = this.calcSortClass(this.sortDir);
  }

  calcSortDir(sorts: Array<SortPropDir>): SortDirection {
    if (sorts && this.column) {
      const sort = sorts.find((s: SortPropDir) => s.prop === this.column.prop);

      if (sort) {
        return sort.dir;
      }
    }
  }

  onSort(): void {
    if (!this.column.sortable) {
      return;
    }

    const newValue = nextSortDir(this.sortType(), this.sortDir);
    this.sort.emit({
      column: this.column,
      prevValue: this.sortDir,
      newValue
    });
  }

  calcSortClass(sortDir: SortDirection): string {
    if (!this.cellContext?.column?.sortable) {
      return;
    }
    if (sortDir === SortDirection.ASC) {
      return `sort-btn sort-asc ${this.sortAscendingIcon()}`;
    } else if (sortDir === SortDirection.DESC) {
      return `sort-btn sort-desc ${this.sortDescendingIcon()}`;
    } else {
      return `sort-btn ${this.sortUnsetIcon()}`;
    }
  }

  ariaSortLabel(): string {
    let sortDirectionString;
    if (this.sortDir === SortDirection.DESC) {
      sortDirectionString = 'descending';
    } else if (this.sortDir === SortDirection.ASC) {
      sortDirectionString = 'ascending';
    } else {
      sortDirectionString = 'none';
    }
    return sortDirectionString;
  }
}
